import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, BookOpen, Users, FileText, Award, ChevronLeft, ChevronRight } from 'lucide-react';
import { issueService, articleService, journalFeatureService } from '../services/api';
import sliderService from '../services/sliderService';

const iconMap = { BookOpen, Users, FileText, Award };

function Home() {
  const [latestIssues, setLatestIssues] = useState([]);
  const [latestArticles, setLatestArticles] = useState([]);
  const [sliderImages, setSliderImages] = useState([]);
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % sliderImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + sliderImages.length) % sliderImages.length);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [issuesRes, articlesRes, slidesRes, featuresRes] = await Promise.all([
          issueService.getAll(),
          articleService.getAll(),
          sliderService.getAll(),
          journalFeatureService.getAll(),
        ]);
        setLatestIssues(issuesRes.data.slice(0, 3));
        setLatestArticles(articlesRes.data.slice(0, 3));
        setSliderImages(slidesRes.data.sort((a, b) => a.order - b.order));
        setFeatures(featuresRes.data || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (sliderImages.length === 0) return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % sliderImages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [sliderImages.length]);

  return (
    <div className="bg-gray-50">
      {/* Hero Section - Full Width Slider */}
      <section className="relative w-full h-[500px] md:h-[600px] overflow-hidden">
        {sliderImages.length > 0 ? (
          <>
            {/* Slider Background Image */}
            <img
              src={sliderImages[currentSlide]?.image}
              alt={sliderImages[currentSlide]?.title}
              className="absolute inset-0 w-full h-full object-cover transition-all duration-700"
            />
            {/* Dark Overlay */}
            <div className="absolute inset-0 bg-gradient-to-l from-black/70 via-black/50 to-black/70"></div>
          </>
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900 to-blue-800"></div>
        )}

        {/* Text Content Overlay */}
        <div className="absolute inset-0 z-10 flex items-center">
          <div className="max-w-7xl mx-auto px-4 w-full">
            <div className="text-center lg:text-right max-w-2xl lg:mr-0 lg:ml-auto">
              <h1 className="hero-title text-5xl md:text-6xl mb-6 leading-tight text-white">نشریه مجری</h1>
              <p className="hero-subtitle text-xl md:text-2xl text-blue-100 mb-8">
                نشریه علمی سازمان مجری ساختمانها و تأسیسات دولتی و عمومی
              </p>
              <p className="hero-subtitle text-lg text-blue-200 mb-12">
                انتشار دانش علمی و تحقیقات برتر در حوزه معماری، مهندسی و مدیریت پروژه‌های ساختمانی
              </p>
              <div className="flex gap-4 justify-center lg:justify-end flex-wrap">
                <Link
                  to="/submit"
                  className="bg-white text-blue-900 px-8 py-4 rounded-lg font-bold hover:bg-blue-50 transition shadow-lg hover:shadow-xl flex items-center gap-2"
                >
                  <span>ارسال مقاله</span>
                  <ArrowLeft size={20} />
                </Link>
                <Link
                  to="/articles"
                  className="border-2 border-white text-white px-8 py-4 rounded-lg font-bold hover:bg-white hover:text-blue-900 transition"
                >
                  مشاهده مقالات
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Slide Info */}
        {sliderImages.length > 0 && (
          <div className="absolute bottom-20 left-4 md:left-10 z-10">
            <h3 className="text-2xl font-bold mb-1 text-white">{sliderImages[currentSlide]?.title}</h3>
            <p className="text-blue-100">{sliderImages[currentSlide]?.description}</p>
          </div>
        )}

        {/* Slider Controls */}
        {sliderImages.length > 1 && (
          <>
            <button
              onClick={prevSlide}
              className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-10 bg-white/20 hover:bg-white/40 text-white p-3 rounded-full transition backdrop-blur-sm"
            >
              <ChevronRight size={24} />
            </button>
            <button
              onClick={nextSlide}
              className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-10 bg-white/20 hover:bg-white/40 text-white p-3 rounded-full transition backdrop-blur-sm"
            >
              <ChevronLeft size={24} />
            </button>
          </>
        )}

        {/* Dots Indicator */}
        {sliderImages.length > 1 && (
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex gap-2">
            {sliderImages.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentSlide(idx)}
                className={`h-3 rounded-full transition ${
                  idx === currentSlide ? 'bg-white w-8' : 'bg-white/50 w-3'
                }`}
              />
            ))}
          </div>
        )}
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">ویژگی‌های نشریه</h2>
            <p className="text-lg text-gray-600">استانداردهای بین‌المللی در انتشار مقالات علمی</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.length ? features.map((feature, idx) => {
              const Icon = iconMap[feature.icon] || BookOpen;
              return (
                <div key={idx} className="bg-gradient-to-br from-blue-50 to-gray-50 p-8 rounded-xl border border-blue-100 hover:border-blue-300 transition hover:shadow-lg">
                  <div className="w-14 h-14 bg-blue-700 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              );
            }) : (
              <div className="col-span-full text-center text-gray-500">ویژگی‌ای برای نمایش وجود ندارد</div>
            )}
          </div>
        </div>
      </section>

      {/* Latest Issues Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between items-center mb-12">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-2">آخرین شماره‌ها</h2>
              <p className="text-gray-600">جدیدترین انتشارات نشریه مجری</p>
            </div>
            <Link to="/issues" className="flex items-center gap-2 text-blue-700 hover:text-blue-900 font-semibold">
              مشاهده همه
              <ArrowLeft size={20} />
            </Link>
          </div>
          {loading ? (
            <div className="text-center py-12 text-gray-500">در حال بارگذاری...</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {latestIssues.map((issue) => (
                <Link
                  key={issue.id}
                  to={`/issues/${issue.id}`}
                  className="bg-white rounded-xl shadow-md hover:shadow-xl transition overflow-hidden group"
                >
                  <div className="bg-gradient-to-br from-blue-700 to-blue-900 text-white p-8 group-hover:from-blue-800 group-hover:to-blue-950 transition">
                    <div className="text-5xl font-bold mb-2">{issue.volume}</div>
                    <p className="text-blue-100">جلد {issue.volume} - شماره {issue.number}</p>
                  </div>
                  <div className="p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-3">{issue.title}</h3>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">{issue.description}</p>
                    <div className="flex justify-between items-center text-sm text-gray-500 pt-4 border-t border-gray-200">
                      <span className="flex items-center gap-1">
                        <FileText size={16} />
                        {issue.articles?.length || 0} مقاله
                      </span>
                      <span>{new Date(issue.publicationDate).toLocaleDateString('fa-IR')}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Latest Articles Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between items-center mb-12">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-2">آخرین مقالات</h2>
              <p className="text-gray-600">مقالات جدید منتشر شده</p>
            </div>
            <Link to="/articles" className="flex items-center gap-2 text-blue-700 hover:text-blue-900 font-semibold">
              مشاهده همه
              <ArrowLeft size={20} />
            </Link>
          </div>
          {loading ? (
            <div className="text-center py-12 text-gray-500">در حال بارگذاری...</div>
          ) : (
            <div className="space-y-4">
              {latestArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/articles/${article.id}`}
                  className="block bg-white border border-gray-200 rounded-lg p-6 hover:border-blue-300 hover:shadow-md transition group"
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-lg font-bold text-gray-900 group-hover:text-blue-700 transition flex-1">{article.title}</h3>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ml-4 ${
                      article.status === 'Published' ? 'bg-green-100 text-green-800' :
                      article.status === 'Under Review' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {article.status === 'Published' ? '✓ منتشر شده' :
                       article.status === 'Under Review' ? '⧗ در حال بررسی' :
                       '✎ پیش‌نویس'}
                    </span>
                  </div>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">{article.abstract}</p>
                  <div className="flex justify-between items-center text-sm text-gray-500 pt-4 border-t border-gray-100">
                    <span className="flex items-center gap-2">
                      <Users size={16} />
                      {article.articleAuthors?.map(aa => aa.author.firstName + ' ' + aa.author.lastName).join('، ')}
                    </span>
                    <span>{new Date(article.submissionDate).toLocaleDateString('fa-IR')}</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-900 to-blue-800 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">آماده برای انتشار مقاله خود؟</h2>
          <p className="text-xl text-blue-100 mb-8">
            نشریه مجری برای دریافت مقالات علمی و تحقیقی در حوزه معماری و مهندسی ساختمان آماده است.
          </p>
          <Link
            to="/submit"
            className="inline-flex items-center gap-2 bg-white text-blue-900 px-10 py-4 rounded-lg font-bold hover:bg-blue-50 transition shadow-lg hover:shadow-xl"
          >
            <span>ارسال مقاله</span>
            <ArrowLeft size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
}

export default Home;
